import React from "react";
import { type NextPage } from "next";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { StackedLogos } from "components/logos";
import {
  DestinationMarquee,
  IndustryLeaderAwards,
  ProductDemo,
  SecurityFeatures,
  TraditionalCdpVsHightouch,
} from "components/content";
import { handleClientError } from "lib/errors";
import { CustomerStatsHeroAnimation, GradientHalfHero } from "components/hero";
import { SolidPlate } from "ui/plates";
import { TextTitle } from "ui/text";
import { CenteredQuote } from "components/quotes";
import { DataSecurityDiptychs, HightouchFeaturesOverview } from "components/solutions/financial-services";
import { implementationEffortComparison } from "components/content/traditional-cdp-vs-hightouch/data";
import Balancer from "react-wrap-balancer";
import heroBgPhoto from "../../../public/assets/solutions/financial-services/hero-bg-photo.png";
import productScreen from "../../../public/assets/solutions/financial-services/accounts-product-screen.png";
import productScreenMobile from "../../../public/assets/solutions/financial-services/accounts-product-screen-mobile.png";

const FinancialServices: NextPage<LandingPageProps> = ({ page, announcementBanner, destinations, customers }) => {
  const ramp = getSafeCustomer(customers, "ramp");
  const chime = getSafeCustomer(customers, "chime");

  if (!ramp || !chime) {
    handleClientError(
      "One of the expected featured customers on the /solutions/advertising page was not found in Sanity.",
    );
  }

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.yellow.150"
      footerOptions={{ variant: "yellow" }}
    >
      <GradientHalfHero
        variant="yellow"
        eyebrow={page.eyebrow}
        title={<Balancer>{page.title}</Balancer>}
        description={page.description}
        media={
          <CustomerStatsHeroAnimation
            bgPhoto={{
              src: heroBgPhoto,
              alt: "An single person in business professional clothing using a tablet.",
            }}
            primaryCaseStudyStat={{
              customer: ramp,
              percentValue: 25,
              description: "Increase in new business",
            }}
            secondaryCaseStudyStat={{
              customer: chime,
              percentValue: 20,
              description: "Lift to performance marketing match rates",
            }}
          />
        }
        mb={{ sm: "small", md: "big", lg: 0 }}
      />

      {page.customers && (
        <StackedLogos
          title="Trusted by leading financial services companies"
          customers={page.customers}
          mobileCustomers={page.customers}
          my="0"
          pb={sectionSpacingY}
        />
      )}

      <SolidPlate theme="dark" bgColor="new.yellow.950" spotlightColor="new.yellow.900">
        <TextTitle
          theme="dark"
          size="lg"
          align="center"
          title="Maximize ROI of every tool with a Composable CDP"
          mb={{ base: "biggest", md: "huge" }}
        />

        <HightouchFeaturesOverview />

        <CenteredQuote
          my={{ base: "huge", md: "hugest" }}
          theme="dark"
          maxW="4xl"
          quoteText="With Hightouch, Snowflake, and dbt, we can go from zero to one as fast as possible."
          author={{
            name: "Kevin Chao",
            job: "Sr. Analytics Engineer",
          }}
          company={{
            name: "Ramp",
            logo: ramp.logoUrl,
          }}
          cta={{
            text: "Read story",
            href: "/customers/ramp",
          }}
        />

        <ProductDemo
          theme="dark"
          showcaseImageVariant="poppy"
          productImage={productScreen}
          productImageAlt='An example of the Hightouch Audiences user interface. There is an audience filter named "USA New Accounts" defined, with two criteria defined: that the customer "Country" equals "USA", and that the customer has "greater than or equal to 1" record in "Accounts", where the "acquisition_date" is within "1 month ago".'
          mobileProductImage={productScreenMobile}
          mobileProductImageAlt='An example of the Hightouch Audiences user interface. There is an audience filter named "USA New Accounts" defined, with the match criteria that the customer "Country" equals "USA".'
          demoEmbedText="Check out this 5 minute interactive demo to learn how you can sync customer data to any business tool."
          demoEmbedCtaText="Start exploring"
        />
      </SolidPlate>

      <TraditionalCdpVsHightouch
        title="The Best CDP for Finance"
        description="Don’t put yourself through a long and painful implementation with a traditional CDP, just to fail months later. Bring your own schema and use your existing data to maximize revenue."
        my="0"
        py={sectionSpacingY}
        rows={[
          {
            category: "Data storage & governance",
            traditionalCdp: {
              title: "Copies data outside of your infrastructure",
              description:
                "Traditional CDPs are built on duplicative data storage — your database and theirs. This increases your data governance risks and creates more data silos for you to manage.",
            },
            hightouch: {
              title: "Doesn’t store data and offers robust governance",
              description:
                "Hightouch operates within your data infrastructure, vastly simplifying data governance. You have granular control over how data can and cannot be used.",
            },
          },
          {
            category: "Data source flexibility",
            traditionalCdp: {
              title: "Operates on a narrow silo of customer behaviors",
              description:
                "Traditional CDPs were just built to handle user and behavioral event data from websites and mobile apps. It’s expensive (or impossible) to integrate data from your many other systems, or organize it into non-user entities like credit lines.",
            },
            hightouch: {
              title: "Takes action on any permitted data source",
              description:
                "Your data warehouse can ingest, organize, and govern data from any of your systems, for any entities that matter to your business and customers. Hightouch enables you to act on your complete data.",
            },
          },
          implementationEffortComparison,
        ]}
      />

      <SecurityFeatures titleAs="h3" my={0} />

      <DataSecurityDiptychs />

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my="0" py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/financial-services", {
  loadDestinations: "all",
  loadCustomers: ["ramp", "chime"],
});

export default FinancialServices;
